import { Button, Center, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useMemo, useState } from 'react'

import { useUser } from '@/client/context/user'
import RPC from '@/client/rpc'
import {
  DETECTED_ISSUE_CATEGORIES,
  DETECTED_ISSUE_TYPE_TO_LABEL,
  DETECTED_ISSUE_TYPE_TO_URGENCY,
  DETECTED_ISSUE_TYPES_BY_CATEGORY,
  SELECTABLE_ISSUE_CATEGORIES,
  SELECTABLE_ISSUE_CATEGORY_TO_DISPLAY_INFO,
} from '@/client/services/moderation/constants'
import hasAutomatedActions from '@/client/services/moderation/hasAutomatedActions'
import type {
  DetectedIssueCategory,
  DetectedIssueType,
  ModeratedEntity,
  ModeratedEntityType,
  SelectableIssueCategory,
} from '@/client/services/moderation/types'
import { getCannyBugReportUrl } from '@/client/services/url'
import { getArticleUrl } from '@/client/services/url/faq'
import { hasUserRole } from '@/client/services/user'

import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'
import Form from '@/components/UI/Form/Form'
import InputRadioButtons from '@/components/UI/Form/FormInput/CustomComponents/InputRadioButtons'
import type { FormBlock, FormInputType, FormValue, FormValues } from '@/components/UI/Form/types'
import Icon from '@/components/UI/Icon/Icon'
import ModalV3 from '@/components/UI/Modal/ModalV3'

function isDetectedIssueCategory(category: string): category is DetectedIssueCategory {
  return DETECTED_ISSUE_CATEGORIES.includes(category as DetectedIssueCategory)
}

const ReportModalV2RadioButtons: React.FC<{
  input: FormInputType
}> = ({ input }) => (
  <InputRadioButtons
    input={input}
    valueToIcon={v => SELECTABLE_ISSUE_CATEGORY_TO_DISPLAY_INFO[v as SelectableIssueCategory].icon}
    iconColor="fg.subtle"
    buttonProps={{
      justifyContent: 'flex-start',
      width: '100%',
    }}
  />
)

export default function ReportModalV2({
  entity,
  reportedUserName,
  open,
  onClose,
  onSubmit,
  contentFlagId,
  skipSubmittedModal = false,
}: {
  entity: ModeratedEntity<ModeratedEntityType>
  reportedUserName: string
  open: boolean
  onClose: () => void
  onSubmit?: () => Promise<void> | void
  contentFlagId?: number
  skipSubmittedModal?: boolean
}): JSX.Element {
  const [issueCategoryInternal, setIssueCategoryInternal] =
    useState<SelectableIssueCategory | null>(null)
  const [showAutomatedActionAlert, setShowAutomatedActionAlert] = useState(false)
  const [showSubmittedModal, setShowSubmittedModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const user = useUser()

  const reportIssueFormInputs: FormBlock[] = useMemo(
    () => [
      {
        label: 'What type of concern are you reporting?',
        type: 'radio',
        name: 'issueCategory',
        initialValue: null,
        options: SELECTABLE_ISSUE_CATEGORIES.map(category => ({
          label: SELECTABLE_ISSUE_CATEGORY_TO_DISPLAY_INFO[category].label,
          value: category,
        })),
        required: true,
        size: 6,
        direction: 'vertical',
        Component: ReportModalV2RadioButtons,
      },
      {
        label: 'More specifically, what is the issue?',
        dependencies: {
          keys: ['issueCategory'],
          callback(values) {
            return (
              (values.issueCategory as string[]).length > 0 &&
              issueCategoryInternal != null &&
              isDetectedIssueCategory(issueCategoryInternal) &&
              DETECTED_ISSUE_TYPES_BY_CATEGORY[issueCategoryInternal].length > 1
            )
          },
        },
        type: 'radio',
        name: 'issueType',
        options:
          issueCategoryInternal != null && isDetectedIssueCategory(issueCategoryInternal)
            ? DETECTED_ISSUE_TYPES_BY_CATEGORY[issueCategoryInternal].map(issueType => ({
                label: `${DETECTED_ISSUE_TYPE_TO_LABEL[issueType]}${
                  DETECTED_ISSUE_TYPE_TO_URGENCY[issueType] === 'high' ? ' [URGENT]' : ''
                }`,
                value: issueType,
              }))
            : [],
        Component: InputRadioButtons,
        required: true,
        size: 6,
      },
      {
        label: 'Please provide more details about the issue.',
        type: 'textarea',
        name: 'content',
        dependencies: {
          keys: ['issueCategory'],
          callback(values) {
            return (
              (values.issueCategory as string).length > 0 &&
              isDetectedIssueCategory(`${values.issueCategory}`)
            )
          },
        },
        required: true,
        rows: 3,
        size: 6,
      },
    ],
    [issueCategoryInternal]
  )

  const handleOnChange = useCallback((name: string, value: FormValue) => {
    if (name === 'issueCategory') {
      setIssueCategoryInternal(value as DetectedIssueCategory)
    }
    if (name === 'issueType') {
      setShowAutomatedActionAlert(hasAutomatedActions(value as DetectedIssueType))
    }
  }, [])

  const handleReport = useCallback(
    async (values: FormValues) => {
      setSubmitting(true)

      let issueType = (values.issueType ?? null) as DetectedIssueType | null

      if (issueCategoryInternal != null && !isDetectedIssueCategory(issueCategoryInternal)) {
        throw new Error('Cannot report an issue in this category')
      }

      if (issueType == null) {
        // When an issue category only has one issue type, we infer the type
        // rather than have the user select it
        if (
          issueCategoryInternal == null ||
          DETECTED_ISSUE_TYPES_BY_CATEGORY[issueCategoryInternal].length !== 1
        ) {
          throw new Error('Expected issueType to be selected')
        }

        // eslint-disable-next-line prefer-destructuring
        issueType = DETECTED_ISSUE_TYPES_BY_CATEGORY[issueCategoryInternal][0]
      }

      const reportIssueArgs = {
        entity,
        issueType,
        content: `${values.content}`,
        contentFlagId,
      }

      try {
        if (hasUserRole(user, 'admin:policy')) {
          await RPC.admin_reportIssueAdmin(reportIssueArgs)
        } else {
          await RPC.reportIssue(reportIssueArgs)
        }

        await onSubmit?.()
      } finally {
        setSubmitting(false)
      }

      if (!skipSubmittedModal) {
        setShowSubmittedModal(true)
      }
    },
    [contentFlagId, entity, skipSubmittedModal, issueCategoryInternal, onSubmit, user]
  )

  return (
    <ModalV3
      size="2xl"
      open={open}
      onClose={onClose}
      title={showSubmittedModal ? null : `Report ${reportedUserName}`}
      disableClose={submitting}
      disableClickOutside={submitting}
      body={() => {
        if (showSubmittedModal) {
          return (
            <Center display="flex">
              <Stack
                display="flex"
                textAlign="center"
                alignItems="center"
                spacing="18px"
                maxWidth="562px"
                pb="32px"
              >
                <Icon faIcon={faPaperPlane} size="3xl" color="gray.emphasized" />
                <Heading as="h5" size="xl">
                  Your concern has been shared!
                </Heading>
                <Text>
                  Thanks for helping us keep our community healthy and safe. Our team will keep what
                  you shared confidential as we work to address the issue as soon as possible.
                </Text>
              </Stack>
            </Center>
          )
        }

        return (
          <Stack spacing={6}>
            <Text>
              If you have a concern about an interaction on Schoolhouse, please let us know! Our
              safety team will privately look into the situation and intervene as needed. You can
              learn more about how this works in our{' '}
              <ShwLink href={getArticleUrl('safety-report-faq')}>safety reporting guide</ShwLink>.
            </Text>
            <Form
              name="ReportIssue"
              blocks={reportIssueFormInputs}
              onSubmit={handleReport}
              onChange={handleOnChange}
              FooterComponent={() => (
                <Stack direction="column" spacing={0}>
                  {issueCategoryInternal === 'technical-issue' ? (
                    <Alert variant="warning" mt="16px">
                      Please post any technical issues on the{' '}
                      <ShwLink href={getCannyBugReportUrl()}>Feedback & Ideas page.</ShwLink>
                    </Alert>
                  ) : showAutomatedActionAlert ? (
                    <Alert variant="info" mt="16px">
                      Note: when you submit your report about {reportedUserName}, automated actions
                      may be taken to hide the content and warn the user about the possible policy
                      violation.
                    </Alert>
                  ) : null}
                  <Flex direction="row" mt={9} justifyContent="flex-end">
                    <Button
                      isDisabled={issueCategoryInternal === 'technical-issue'}
                      isLoading={submitting}
                      type="submit"
                    >
                      Submit
                    </Button>
                  </Flex>
                </Stack>
              )}
            />
          </Stack>
        )
      }}
    />
  )
}
